import type { Locale, PageId } from '../i18n/locale';
import { ROUTES } from '../i18n/locale';

/**
 * Shared public-site content per locale (es-CO canonical, en). HOME copy lives in src/home/content.ts,
 * intake copy in src/intake/content.ts. Launch product: COLESCOMEX SUPPLY — international procurement
 * and supply-chain operator for structured Colombian B2B companies (docs/bos/03, docs/bos/04).
 */
export const INTAKE_PATH = ROUTES.intake['es-CO'];
/** Session-storage key for a need typed on the HOME before opening the intake (never sent in the URL). */
export const PREFILL_KEY = 'cx-intake-prefill';

/** HOME section anchors, localized so English deep links read in English. */
export const SECTION_IDS = {
  operate: { 'es-CO': 'como-operamos', en: 'how-we-operate' },
  sourcing: { 'es-CO': 'abastecimiento', en: 'sourcing' },
  industries: { 'es-CO': 'industrias', en: 'industries' },
  visibility: { 'es-CO': 'visibilidad', en: 'visibility' },
  problem: { 'es-CO': 'una-operacion', en: 'one-operation' },
  need: { 'es-CO': 'necesidad', en: 'your-need' },
  process: { 'es-CO': 'pasos', en: 'steps' },
  economics: { 'es-CO': 'costo-total', en: 'total-cost' },
  journey: { 'es-CO': 'del-origen-a-colombia', en: 'origin-to-colombia' },
  start: { 'es-CO': 'iniciar', en: 'start' },
} as const satisfies Record<string, Record<Locale, string>>;
export type SectionKey = keyof typeof SECTION_IDS;
export const sectionId = (key: SectionKey, locale: Locale) => SECTION_IDS[key][locale];
/** Maps a HOME hash to the same section in another locale (used by the language switch). */
export function translateHash(hash: string, to: Locale): string {
  const id = hash.replace(/^#/, ''); if (!id) return '';
  for (const ids of Object.values(SECTION_IDS)) if (Object.values(ids).includes(id as never)) return `#${ids[to]}`;
  return '';
}

export interface SiteContent {
  skip: string;
  homeLabel: string;
  nav: { key: SectionKey; label: string }[];
  start: string;
  menu: { open: string; close: string; label: string };
  language: { label: string; current: string; switchTo: string };
  descriptor: string;
  statement: string;
  footerNav: string;
  rights: string;
  meta: Record<PageId, { title: string; description: string }>;
}

export const SITE: Record<Locale, SiteContent> = {
  'es-CO': {
    skip: 'Saltar al contenido',
    homeLabel: 'COLESCOMEX, inicio',
    nav: [
      { key: 'operate', label: 'Cómo operamos' },
      { key: 'sourcing', label: 'Abastecimiento' },
      { key: 'industries', label: 'Industrias' },
      { key: 'visibility', label: 'Visibilidad' },
    ],
    start: 'Iniciar proyecto',
    menu: { open: 'Abrir menú', close: 'Cerrar menú', label: 'Principal' },
    language: { label: 'Idioma', current: 'Español', switchTo: 'English version' },
    descriptor: 'Procurement · Supply chain · Comercio exterior',
    statement: 'Procurement internacional y supply chain para empresas en Colombia.',
    footerNav: 'Pie de página',
    rights: 'Colombia.',
    meta: {
      home: {
        title: 'COLESCOMEX | Procurement internacional y supply chain para empresas',
        description: 'COLESCOMEX estructura y ejecuta compras internacionales para empresas colombianas: proveedores, cumplimiento, calidad, logística, importación y entrega bajo una sola operación.',
      },
      intake: {
        title: 'Iniciar proyecto | COLESCOMEX',
        description: 'Cuéntenos qué necesita comprar su empresa. COLESCOMEX estructura el requerimiento y revisa su viabilidad antes de cotizar.',
      },
    },
  },
  en: {
    skip: 'Skip to content',
    homeLabel: 'COLESCOMEX, home',
    nav: [
      { key: 'operate', label: 'How we operate' },
      { key: 'sourcing', label: 'Sourcing' },
      { key: 'industries', label: 'Industries' },
      { key: 'visibility', label: 'Visibility' },
    ],
    start: 'Start a project',
    menu: { open: 'Open menu', close: 'Close menu', label: 'Main' },
    language: { label: 'Language', current: 'English', switchTo: 'Versión en español' },
    descriptor: 'Procurement · Supply chain · International trade',
    statement: 'International procurement and supply chain for companies in Colombia.',
    footerNav: 'Footer',
    rights: 'Colombia.',
    meta: {
      home: {
        title: 'COLESCOMEX | International Procurement & Supply Chain',
        description: 'COLESCOMEX structures and executes international procurement for Colombian companies, from supplier sourcing and compliance to quality, logistics, import and delivery.',
      },
      intake: {
        title: 'Start a project | COLESCOMEX',
        description: 'Tell us what your company needs to purchase. COLESCOMEX structures the requirement and reviews its feasibility before quoting.',
      },
    },
  },
};

/**
 * Commercial contact route. Only a mailbox configured at build time (VITE_PUBLIC_CONTACT_EMAIL) is
 * shown; no address is hard-coded until one is verified for production.
 */
export const CONTACT_EMAIL = ((import.meta.env?.VITE_PUBLIC_CONTACT_EMAIL as string | undefined) ?? '').trim() || null;
